import { startOfDay } from "@app/core";
import { useLayoutEffect, useRef } from "react";
import { animate, springs, useMotionValue, useReducedMotion, useTransform, type MotionValue } from "../../lib/motion/index.js";

const DAY_MS = 86_400_000;
/** Soft no-overshoot spring (~270ms settle), with DAY-unit rest thresholds
 *  (the pan value is in day columns, not px). A spring (not a duration ease)
 *  so each retarget CARRIES the in-flight velocity — rapid stepping reads as
 *  one continuous glide. */
const PAN_SPRING = { ...springs.gentle, restDelta: 0.003, restSpeed: 0.05 };

/** Imperative finger-tracking handle for a 1-finger mobile horizontal drag.
 *  `begin` captures the current translate; `moveByDays` offsets it live (in DAY
 *  units, so it composes with the %-of-track render); `settle` springs back to
 *  rest (used when the drag committed no day shift — a shift lets the layout
 *  effect animate to the new rest instead). */
export interface DayPanDrag {
  begin: () => void;
  moveByDays: (deltaDays: number) => void;
  settle: () => void;
  /** Seed the NEXT commit animation's initial velocity (day-units/sec) so a
   *  release flick glides to rest with momentum instead of stopping dead. */
  setVelocity: (unitsPerSec: number) => void;
}

export interface DayPan {
  /** Shared translateX (a "%" string of the TRACK's own width) for the
   *  header / all-day / timed tracks. */
  x: MotionValue<string>;
  /** Live drag controls for the mobile touch pan. */
  drag: DayPanDrag;
}

/**
 * Translates the (stable, wide) day track so the visible window fills the
 * viewport. A visible-window step animates the translate from its current spot
 * to the new rest — the whole track slides as one rigid unit, so grid lines and
 * task blocks move in perfect unison. A window RECENTER (same dates re-keyed
 * around a new origin) shifts the translate by the window delta — visually a
 * no-op — and an in-flight pan continues to the new rest uninterrupted.
 *
 * The pan value lives in DAY units and is rendered as a PERCENT of the track,
 * never in px: nothing here measures the viewport, so a continuous viewport
 * resize (sidebar collapse/expand/drag, window resize) costs no React work —
 * the %-sized columns and the %-translate scale natively with the browser's
 * own layout, every frame.
 */
export function useDayPan(
  windowStart: Date,
  visibleStart: Date,
  totalDays: number,
): DayPan {
  const xDays = useMotionValue(0);
  const reduce = useReducedMotion();
  const prevVis = useRef<number | undefined>(undefined);
  const prevWin = useRef<number | undefined>(undefined);
  // The in-flight settle animation, tracked so a touch-drag can stop it.
  const animRef = useRef<ReturnType<typeof animate> | null>(null);
  const dragBaseline = useRef(0);
  // Velocity (day-units/sec) seeded by a release flick for the next commit.
  const flingVelocity = useRef<number | null>(null);

  const winTime = startOfDay(windowStart).getTime();
  const visTime = startOfDay(visibleStart).getTime();
  const restDays = -Math.round((visTime - winTime) / DAY_MS);
  const restRef = useRef(restDays);
  restRef.current = restDays;

  useLayoutEffect(() => {
    const pv = prevVis.current;
    const pw = prevWin.current;
    prevVis.current = visTime;
    prevWin.current = winTime;
    // First mount / reduced motion: place at rest.
    if (pv === undefined || reduce || (pv === visTime && pw === winTime)) { xDays.set(restDays); return; }
    // Capture the in-flight velocity BEFORE any compensation set, and feed it
    // to the retargeting spring. A fixed-duration ease restarted per click
    // spiked then decayed the speed before the next click landed, so constant-
    // cadence stepping pulsed (surge–stall–surge).
    // Prefer a flick-seeded velocity (set on release) over the sampled one — by
    // the time this effect runs the gesture's last set() is a frame stale, so
    // getVelocity() reads ~0 and the fling would stop dead.
    const velocity = flingVelocity.current ?? xDays.getVelocity();
    flingVelocity.current = null;
    // A recenter re-keys the SAME dates around a new origin. Shift the current
    // translate by the window delta so the visual position is unchanged, then
    // let any in-flight pan CONTINUE to the new rest — snapping straight to
    // rest here teleported a mid-flight pan to its end, so rapid week
    // stepping alternated animate/jump instead of keeping pace.
    if (pw !== undefined && pw !== winTime) {
      // jump(), not set() — the teleport delta must not poison getVelocity().
      xDays.jump(xDays.get() + Math.round((winTime - pw) / DAY_MS));
    }
    if (Math.abs(xDays.get() - restDays) < 0.003 && Math.abs(velocity) < 0.05) { xDays.set(restDays); return; }
    animRef.current = animate(xDays, restDays, { ...PAN_SPRING, velocity });
    return () => animRef.current?.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visTime, winTime]);

  // Render as a % of the track's own width (CSS translate percentages refer to
  // the element itself). Day count via a ref so the latest value is used even
  // when only the window size changed between notifications.
  const totalRef = useRef(totalDays);
  totalRef.current = totalDays;
  const x = useTransform(xDays, (d) => `${(d / Math.max(1, totalRef.current)) * 100}%`);

  // Stable drag handle (methods close over the stable motion value + refs).
  const drag = useRef<DayPanDrag>({
    begin() { animRef.current?.stop(); dragBaseline.current = xDays.get(); },
    moveByDays(deltaDays) { xDays.set(dragBaseline.current + deltaDays); },
    settle() { animRef.current?.stop(); animRef.current = animate(xDays, restRef.current, { ...PAN_SPRING, velocity: flingVelocity.current ?? xDays.getVelocity() }); flingVelocity.current = null; },
    setVelocity(unitsPerSec) { flingVelocity.current = unitsPerSec; },
  }).current;

  return { x, drag };
}
